import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NotificationService } from '@app/core';
import { ServiceData, ServiceSelectorData, CreateServiceData } from '@app/models/program-management.model';
import { GENERAL_MESSAGES, SNACKBAR_ACTIONS } from '@app/shared/data/messages';

@Component({
  selector: 'app-service-selector',
  templateUrl: './service-selector.component.html',
  styleUrls: ['./service-selector.component.scss'],
  standalone: false
})
export class ServiceSelectorComponent implements OnInit {

  generalMessages = GENERAL_MESSAGES;
  @Input('serviceData') serviceData: ServiceSelectorData | null = null;
  @Input('maxLimit') maxLimit: number = 10;
  @Input('disabled') disabled: boolean = false;
  @Output('valueChanges') valueChanges: EventEmitter<any> = new EventEmitter<any>();
  @Output('createServiceEmitter') createServiceEmitter: EventEmitter<CreateServiceData> = new EventEmitter<CreateServiceData>();
  @ViewChild('sheetTemp') sheetTemp!: TemplateRef<any>;

  serviceValues: { id: number; name: string; }[] = [];
  serviceListValues: { id: number; name: string; checked: boolean; }[] = [];

  searchInputValue: string = "";
  searchInputErrors: string[] = [];
  searchPanelOpened: boolean = false;
  maxLimitErrorFlag: boolean = false;
  createRequestInProgress: boolean = false;

  constructor(
    private readonly _bottomSheet: MatBottomSheet,
    private readonly _notificationService: NotificationService,
  ) { }

  openSheet(): void {
    this.setCurrentValues();
    this._bottomSheet.open(this.sheetTemp, {
      panelClass: 'service-selector-sheet',
      disableClose: true
    });
  }

  setCurrentValues(checkedServices: number[] = []): void {
    const prevSelectedIDs = Array.from(
      new Set([
        ...this.serviceValues.map(service => service.id),
        ...checkedServices
      ])
    );

    this.serviceListValues =
      this.serviceData?.availableServices?.map(service => ({
        id: service.id,
        name: service.serviceTypeName,
        checked: prevSelectedIDs.includes(service.id)
      })) ?? [];
  }

  ngOnInit(): void {
    this.initiateValues();
  }

  initiateValues(data: ServiceSelectorData | null = this.serviceData): void {
    this.resetSearch();
    this.serviceData = data;
    this.serviceValues = [];
    this.serviceListValues = [];
    const selectedIds = new Set(data?.values ?? []);
    data?.availableServices?.forEach(service => {
      if (selectedIds.has(service.id)) {
        this.serviceValues = [
          ...this.serviceValues,
          { id: service.id, name: service.serviceTypeName }
        ];
      }
    });
  }

  closeForm(resetValues: boolean = false) {
    if (resetValues) {
      let prevSelectedIDs = this.serviceValues.map(service => service.id);
      this.serviceListValues = this.serviceListValues.map(service => ({ ...service, checked: prevSelectedIDs.includes(service.id) }));
    } else {
      this.serviceValues = [...this.serviceListValues.filter(service => service.checked).map(service => ({ id: service.id, name: service.name }))];
    }
    this._bottomSheet.dismiss();
    this.valueChanges.emit();
  }

  removeService(id: number) {
    this.serviceValues = this.serviceValues.filter(service => (service.id !== id));
  }

  listChange(checked: boolean, id: number) {
    this.maxLimitErrorFlag = (checked && this.reachedMaxLimit);
    if (!this.maxLimitErrorFlag) {
      this.serviceListValues = this.serviceListValues.map(service => (service.id === id) ? { ...service, checked: !!checked } : service);
    }
  }

  clearAll() {
    this.serviceListValues = this.serviceListValues.map(service => ({ ...service, checked: false }));
  }

  reset() {
    this.initiateValues(this.serviceData);
  }

  get selectedServices() {
    return this.serviceValues;
  }

  get selectedServiceIds() {
    return this.selectedServices.map(service => service.id).sort((a, b) => a - b);
  }

  get reachedMaxLimit(): boolean {
    return this.serviceListValues.filter(service => !!service.checked).length >= this.maxLimit;
  }

  trackByFn(index: number, item: any): number {
    return item.id;
  }

  get checkedServices() {
    return this.serviceListValues.filter(service => service.checked);
  }

  get searchServices() {
    return this.serviceListValues.filter(service => service.name.toLowerCase().includes(this.searchInputValue.toLowerCase()));
  }

  openSearchPanel() {
    this.searchPanelOpened = true;
    this.maxLimitErrorFlag = this.reachedMaxLimit;
  }

  createService() {
    if (this.searchInputErrors.length > 0) return;
    this.setCreateRequestProgress(true);
    this.createServiceEmitter.emit({serviceTypeName: this.searchInputValue.trim()});
  }

  handleCreateServiceResponse(servicesData: ServiceData[], checkedServiceIds: number[]) {
    this.serviceData = this.serviceData
      ? {
        ...this.serviceData,
        availableServices: [...servicesData]
      }
      : null;
    this.setCurrentValues(checkedServiceIds);
    this.setCreateRequestProgress(false);
    this._notificationService.showSuccess(`New service type "${this.searchInputValue}" created successfully`, SNACKBAR_ACTIONS.OK);
  }

  handleCreateServiceError(error: HttpErrorResponse, checkedServiceIds: number[]) {
    this.setCurrentValues(checkedServiceIds);
    this.setCreateRequestProgress(false);
    this._notificationService.showError(error, SNACKBAR_ACTIONS.OK);
  }

  resetSearch() {
    this.searchInputValue = "";
    this.searchPanelOpened = false;
    this.maxLimitErrorFlag = false;
    this.searchInputErrors = [];
  }

  getValidateServicePattern(): void {
    this.searchInputErrors = [];
    if (!this.searchInputValue.length) return;

    if (this.searchInputValue.length < 3) {
      this.searchInputErrors.push('Enter minimum 3 characters.');
    }
  }

  setCreateRequestProgress(value: boolean = !this.createRequestInProgress) {
    this.createRequestInProgress = value;
  }

}